import { isNil } from 'lodash-es';
import { defineStore } from 'pinia';
import { useDrawerStore } from '~/stores/drawer';

interface OpenDatatableDrawerParams {
  /** The specific datatable component to load in the drawer */
  componentName: string;
  /** Variables to pass to the datatable's query */
  variables?: Record<string, any>;
  /** The title to display in the drawer header */
  title: string;
  /** Default sorting to apply if variables.order is not provided */
  defaultSort?: Array<Record<string, any>>;
  /** Optional callback function when the drawer closes */
  onClose?: () => void | Promise<void>;
}

export const useDatatableDrawerStore = defineStore('datatableDrawer', () => {
  const drawerStore = useDrawerStore();

  /**
   * Opens a specified datatable component in a drawer.
   * Applies default sorting if no 'order' is present in the provided variables.
   */
  function openDatatableDrawer({
    componentName,
    variables,
    title,
    defaultSort = [], // Default to empty array if not provided
    onClose,
  }: OpenDatatableDrawerParams) {
    // Start with provided variables or an empty object
    const finalVariables = variables ? { ...variables } : {};

    // Apply default sort only if 'order' is not already defined in variables
    if (
      (isNil(finalVariables.order) || finalVariables.order.length === 0) &&
      defaultSort.length > 0
    ) {
      finalVariables.order = [...defaultSort];
    }

    // Construct the payload for drawerStore.push2
    const drawerPayload: any = {
      componentName,
      params: {
        variables: finalVariables,
      },
      title,
      wrapperStyle: {
        // Consistent styling
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    };

    // Add onClose callback if it exists
    if (onClose) {
      drawerPayload.onClose = onClose;
    }

    // Open the drawer
    drawerStore.push2(drawerPayload);
  }

  return {
    open: openDatatableDrawer,
  };
});
